// index.jsx
import React, { useState, useCallback, useEffect } from 'react';
// MUI Material Imports
import CircularProgress from '@mui/material/CircularProgress';
import { BiGift, BiCopy, BiShareAlt } from 'react-icons/bi';
import { HiOutlineUserAdd, HiOutlineUserRemove } from 'react-icons/hi';
import { Alert, Snackbar } from '@mui/material';
// Profile Components
import ProfileTabs from '../../components/tabs';
import { defaultBanner, getProfileBasics, saveToFollowing, removeFromFollowing, bannerName, } from '../../services/profile';
import { authenticatedUserAddress, isUserSignedIn } from '../../services/auth';
import { isFileRecoverable, validateUsersContract } from '../../services/stacks';
import BannerUpload from './BannerUpload';
// Components imports
import NFTAvatar from '../../components/avatar/NFTAvatar';
import ConfirmModal from '../../components/modal/ConfirmModal';
import ClaimOGBadge from '../../components/avatar/ClaimOGBadge';
import GiftingModal from '../../components/modal/GiftingModal';
import WoopsModal from '../../components/modal/WoopsModal';
import BannerUploadModal from '../../components/modal/BannerUploadModal';
import UserContractDeployModal from '../../components/modal/UserContractDeployModal';
import AdvisoryModal from '../../components/modal/AdvisoryModal';

import { clientNetwork, stacksApi } from '../../lib/constants';
import axios from 'axios';
// Stylers
import './style.scss';
import Avatar from '../../components/customavatar';
import AvatarConfigModal from '../../components/modal/AvatarConfigModal';

const appOrigin = window.location.origin;

const Profile = ({ address, userSettings }) => {
  const [avatar, setAvatar] = useState(null);
  const [authorName, setAuthorName] = useState(null);
  const [authorUser, setAuthorUser] = useState(null);
  const [memberSince, setMemberSince] = useState(null);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [storageUrl, setStorageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userContacts, setUserContacts] = useState({});
  const [errorMsg, setErrorMsg] = useState({ msg: '', reason: 'success' });
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [currentMempool, setCurrentMempool] = useState([]);

  const [userContractStatus, setUserContractStatus] = useState('');
  const [txId, setTxId] = useState('');
  const [tx_Id, setTx_Id] = useState('');

  const [loadOutData, setLoadOutData] = useState(null);

  // Modals state
  const [showBannerUpload, setShowBannerUpload] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showGiftingModal, setShowGiftingModal] = useState(false);
  const [showWoopsModal, setShowWoopsModal] = useState(false);
  const [showUserContractDeployModal, setShowUserContractDeployModal] = useState(false);
  const [showAdvisoryModal, setShowAdvisoryModal] = useState(false);
  const [showAvatarConfigModal, setShowAvatarConfigModal] = useState(false);

  const authenticatedUser = authenticatedUserAddress({ network: clientNetwork });

  function redirect(path) {
    window.open(`${path}`, '_blank');
  }

  async function validating(open, value, userStorage) {
    const { state, txId } = await validateUsersContract(userStorage);
    if (!isLoading) setTxId(txId);
    setUserContractStatus(state);
    switch (state) {
      case true:
        open(value);
        break;
      case false:
      case 'success':
      case 'mempool':
      case 'abort_by_post_condition':
      case 'abort_by_response':
        setShowUserContractDeployModal(true);
        break;
      case 'updateNeeded':
        setShowAdvisoryModal(true);
        break;
      default:
        if (isUserSignedIn()) {
          setShowUserContractDeployModal(true);
        } else {
          setShowWoopsModal(true);
        }
        break;
    }
  }

  const handleFollowClick = async (authorPrincipal) => {
    if (isUserSignedIn()) {
      saveToFollowing(authorPrincipal, setUserContacts);
      setErrorMsg({ msg: `Now following`, reason: 'success' });
      setOpenSnackBar(true);
    } else {
      setShowWoopsModal(true);
    }
  };

  const handleUnfollowClick = async (authorPrincipal) => {
    if (isUserSignedIn()) {
      removeFromFollowing(authorPrincipal, setUserContacts);
      setErrorMsg({ msg: `Now unfollowing`, reason: 'success' });
      setOpenSnackBar(true);
    } else {
      setShowWoopsModal(true);
    }
  };

  const handleGiftClick = () => {
    if (isUserSignedIn()) {
      setShowGiftingModal(true);
    } else {
      setShowWoopsModal(true);
    }
  };

  const handleCopyClick = (user) => {
    navigator.clipboard.writeText(user);
    setErrorMsg({ msg: `Principal Copied`, reason: 'success' });
    setOpenSnackBar(true);
  };

  const handleShareClick = (user) => {
    navigator.clipboard.writeText(`${appOrigin}/${user}`);
    setErrorMsg({ msg: `Profile URL Copied`, reason: 'success' });
    setOpenSnackBar(true);
  };

  const init = async () => {
    try {
      const res = await getProfileBasics(address || authenticatedUser);
      const { address: authedUser, name, deployDate, FuserContacts, userStorage } = res;
      const getMempool = (
        await axios.get(
          `${stacksApi[clientNetwork]}/extended/v1/tx/mempool?sender_address=${authedUser}&unanchored=true`
        )
      ).data?.results;
      if (Array.isArray(getMempool)) setCurrentMempool(getMempool);
      setAuthorUser(authedUser);
      setAuthorName(name);
      setMemberSince(deployDate);
      setUserContacts(FuserContacts);
      setStorageUrl(userStorage);
      const bannerExists = userStorage !== undefined ? await isFileRecoverable(userStorage + bannerName, true) : false;
      setBannerUrl(bannerExists === true && userStorage !== 'default' ? `${userStorage}${bannerName}` : defaultBanner);
      if (isUserSignedIn()) await validating(setShowConfirmModal, false, userStorage);
    } catch (error) {
      console.error('Error loading profile:', { error });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <>
      <div className="profile-box">
        {isLoading && <CircularProgress color="success" />}
        {!isLoading && !authorUser && <div>User Account Not Found!</div>}

        {!isLoading && authorUser && (
          <div className="profile-content">
            <div className="profile-banner">
              <div className="profile-banner-box">
                <img className="profile-banner-image" src={bannerUrl + `?t=${new Date().getTime()}`} alt="banner" />
              </div>

              <div className="profile-info-box">
                <div className="profile-info">
                  <h1 className="profile-name">
                    {authorName || (authorUser !== authenticatedUser) ? 'No Name' : <small><button onClick={() => redirect('https://btc.us/')} id="button">Get Name</button></small>}
                  </h1>
                  <p className="profile-address">
                    {authorUser.substring(0, 5) + ' ... ' + authorUser.substring(authorUser.length - 5)}{' '}
                  </p>
                  <p className="profile-address">
                    {(authorUser === authenticatedUser) &&
                      <>
                        Joined: <small>
                          {memberSince
                            ? memberSince
                            : (userContractStatus === 'notfound' || userContractStatus === false)
                              ? <button onClick={() => setShowUserContractDeployModal(true)} id="button">{userContractStatus === 'notfound' ? 'Deploy Contract' : 'Activate'}</button>
                              : null
                          }
                        </small>
                      </>
                    }
                  </p>
                </div>

                <div className="profile-buttons">
                  {authorUser !== authenticatedUser ? (
                    <div className="action-buttons">
                      {userContacts?.[authorUser] ? (
                        <button className="action-button" onClick={() => handleUnfollowClick(authorUser)}>
                          <HiOutlineUserRemove /> Unfollow{' '}
                        </button>
                      ) : (
                        <button className="action-button" onClick={() => handleFollowClick(authorUser)}>
                          <HiOutlineUserAdd /> Follow{' '}
                        </button>
                      )}
                      <button className="action-button" onClick={() => handleCopyClick(authorUser)}>
                        <BiCopy />{' '}
                      </button>
                      <button className="action-button" onClick={handleGiftClick}>
                        <BiGift />{' '}
                      </button>
                      <button className="action-button" onClick={() => handleShareClick(authorUser)}>
                        <BiShareAlt />{' '}
                      </button>
                    </div>
                  ) : null}

                  <BannerUpload
                    self={authorUser === authenticatedUser}
                    storageUrl={storageUrl}
                    setBannerUrl={setBannerUrl}
                    setErrorMsg={setErrorMsg}
                    setOpenSnackBar={setOpenSnackBar}
                    setShowBannerUpload={setShowBannerUpload}
                    setShowUserContractDeployModal={setShowUserContractDeployModal}
                    setShowAdvisoryModal={setShowAdvisoryModal}
                    setShowWoopsModal={setShowWoopsModal}
                    userContractStatus={userContractStatus}
                  />
                </div>
              </div>

              <div className="avatar-box">
                <div className="profile-picture">
                  {/* <NFTAvatar
                    txId={setTx_Id}
                    setShowConfirmModal={setShowConfirmModal}
                    authorPrincipal={authorUser}
                    AppStoragePath={storageUrl}
                    authorName={authorName}
                    userSettings={userSettings}
                    profile={true}
                  /> */}

                  <Avatar
                    authorPrincipal={authorUser}
                    loadOutUri={storageUrl}
                    userSettings={userSettings}
                    profile={true}
                    setShowUserContractDeployModal={setShowUserContractDeployModal}
                    setShowAdvisoryModal={setShowAdvisoryModal}
                    setShowWoopsModal={setShowWoopsModal}
                    setShowBannerUpload={setShowBannerUpload}
                    userContractStatus={userContractStatus}
                    setShowAvatarConfigModal={setShowAvatarConfigModal}
                    loadOutData={loadOutData}
                    setLoadOutData={setLoadOutData}
                  />

                  <div id="ogcheck-profile-container">
                    <ClaimOGBadge
                      txId={txId}
                      setShowConfirmModal={setShowConfirmModal}
                      authorPrincipal={authorUser}
                      userSettings={userSettings}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="profile-tabs">
              <ProfileTabs
                profileAddress={address}
                authorPrincipal={authorUser}
                userSettings={userSettings}
                setShowUserContractDeployModal={setShowUserContractDeployModal}
                setShowWoopsModal={setShowWoopsModal}
                setOpenConfirmModal={setShowConfirmModal}
                userContractStatus={userContractStatus}
                storageUrl={storageUrl}

                txId={txId}
                setTxId={setTxId}
                tx_Id={tx_Id}
                setTx_Id={setTx_Id}
              />
            </div>
          </div>
        )}
      </div>

      {showAvatarConfigModal && (
        <AvatarConfigModal show={showAvatarConfigModal} close={() => setShowAvatarConfigModal(false)} storageUrl={storageUrl} setLoadOutData={setLoadOutData} loadOutData={loadOutData} setErrorMsg={setErrorMsg} setOpenSnackBar={setOpenSnackBar} setShowUserContractDeployModal={setShowUserContractDeployModal} setShowAdvisoryModal={setShowAdvisoryModal} setShowWoopsModal={setShowWoopsModal} userContractStatus={userContractStatus} setShowBannerUpload={setShowBannerUpload} />
      )}

      {showBannerUpload && (
        <BannerUploadModal show={showBannerUpload} close={() => setShowBannerUpload(false)} currentMempool={currentMempool} storageUrl={storageUrl} txId={txId} setTxId={setTxId} userSettings={userSettings} />
      )}

      {showConfirmModal && (
        <ConfirmModal show={showConfirmModal} txId={tx_Id} close={() => setShowConfirmModal(false)} userSettings={userSettings} />
      )}

      {showGiftingModal && (
        <GiftingModal show={showGiftingModal} setShow={setShowGiftingModal} close={() => setShowGiftingModal(false)} authorPrincipal={authorUser} giftVia={address} userSettings={userSettings} />
      )}

      {showWoopsModal && (
        <WoopsModal show={showWoopsModal} setShow={setShowWoopsModal} close={() => setShowWoopsModal(false)} />
      )}

      {showUserContractDeployModal && (
        <UserContractDeployModal show={showUserContractDeployModal} setShow={setShowUserContractDeployModal} txId={txId} setTxId={setTxId} userContractStatus={userContractStatus} setUserContractStatus={setUserContractStatus} openConfirmModal={setShowConfirmModal} setTx_Id={setTx_Id} userSettings={userSettings} />
      )}

      {showAdvisoryModal && (
        <AdvisoryModal show={showAdvisoryModal} close={() => setShowAdvisoryModal(false)} />
      )}

      <Snackbar open={openSnackBar} autoHideDuration={3000} onClose={() => setOpenSnackBar(false)}      >
        <Alert onClose={() => setOpenSnackBar(false)} severity={errorMsg.reason} sx={{ width: '100%' }}        >
          {errorMsg.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Profile;